// src/components/ConfirmDialog/ConfirmDialog.js
import React, { useEffect, useRef } from 'react';
import styles from './ConfirmDialog.css';

/**
 * Material Design 3 Confirmation Dialog
 * Replacement for native window.confirm()
 */
const ConfirmDialog = ({
    isOpen,
    title = 'Confirmar',
    message = 'Tem certeza que deseja continuar?',
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    confirmVariant = 'primary', // 'primary' | 'danger' | 'warning'
    onConfirm,
    onCancel,
    isLoading = false,
    icon = null,
}) => {
    const confirmButtonRef = useRef(null);
    const dialogRef = useRef(null);

    // Focus trap - auto-focus on confirm button when opened
    useEffect(() => {
        if (isOpen && confirmButtonRef.current) {
            setTimeout(() => confirmButtonRef.current.focus(), 50);
        }
    }, [isOpen]);

    // ESC key to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen && !isLoading) {
                onCancel?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isLoading, onCancel]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const getVariantClass = () => {
        switch (confirmVariant) {
            case 'danger':
                return styles.dangerButton;
            case 'warning':
                return styles.warningButton;
            default:
                return styles.primaryButton;
        }
    };

    return (
        <div
            className={styles.backdrop}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isLoading) {
                    onCancel?.();
                }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
        >
            <div
                className={styles.dialog}
                ref={dialogRef}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.header}>
                    {icon && <span className={styles.icon}>{icon}</span>}
                    <h3 id="confirm-dialog-title" className={styles.title}>
                        {title}
                    </h3>
                </div>

                <p className={styles.message}>{message}</p>

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        ref={confirmButtonRef}
                        className={`${styles.confirmButton} ${getVariantClass()}`}
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className={styles.spinner} />
                                Processando...
                            </>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDialog;